import { useMemo } from 'react'

import { useQuery } from 'react-query'

import { usePagination } from '@webapp/core/hooks/usePagination'
import { ReconcileMatch } from '@webapp/modules/accounts-payable/entities/ReconcileMatch'
import { reconcileSessionQueries } from '@webapp/modules/accounts-payable/query-configs/reconcileSessionQueries'

interface IUseQueryMatchedTransactionsParams {
  sessionId: Nullable<number>
  onError?: (err: unknown) => void
}

interface IUseQueryMatchedTransactionsReturn {
  matches: ReconcileMatch[]
  /** metadata.summary.total_amount — cleared balance (BHV-036) */
  totalAmount: Nullable<number>
  /** pagination.count — FS tab badge (BHV-030) */
  matchedCount: number
  isLoading: boolean
  error: unknown
}

export const useQueryMatchedTransactions = ({
  sessionId,
  onError,
}: IUseQueryMatchedTransactionsParams): IUseQueryMatchedTransactionsReturn => {
  const { params, updateNumPages } = usePagination({
    id: 'ap-reconcile-matched',
  })

  const { data, isLoading, error } = useQuery({
    ...reconcileSessionQueries.matches(sessionId ?? 0, {
      page: params.page,
      pageSize: params.pageSize,
    }),
    enabled: sessionId !== null,
    onSuccess: (res) => {
      updateNumPages(res.pagination.numPages)
    },
    onError,
  })

  const matches = useMemo(
    () =>
      (data?.matches ?? []).map(
        (constructor) => new ReconcileMatch(constructor)
      ),
    [data]
  )

  return {
    matches,
    totalAmount: data?.totalAmount ?? null,
    matchedCount: data?.pagination.count ?? 0,
    isLoading,
    error,
  }
}
